import { Box, Flex } from "@chakra-ui/layout";
import NewTable from "components/NewTable";
import HeaderTable from "components/Page/HeaderTable";
import SelectableButton from "ui/components/Button/SelectableButton";
import {
  EncomendaStatusEnum,
  EncomendaDto,
} from "core/features/encomenda/typings";
import { BsChatDots } from "react-icons/bs";
import { VscFile } from "react-icons/vsc";
import { MdOutlineHistory, MdDelete } from "react-icons/md";
import { TipoDestinoEnum } from "core/features/documentos/enums";
import { AssuntoMensagem } from "core/features/mensagens/typings";
import { useMensagemModal } from "ui/components/DrawerNovaMensagem/MensagemModalContext";
import { FiltroEncomenda } from "./hooks/useFiltroEncomenda";
import colors from "core/resources/theme/colors";
import useEncomendaColumns from "./hooks/useEncomendaColumns";
import { PaginateResult } from "core/resources/types/shared";
import { FaCalendarDay } from "react-icons/fa";
import { Button } from "@chakra-ui/react";
import { PermissaoEnum } from "core/resources/enums/permissao";

interface ListaEncomendaTableProps {
  filtro: FiltroEncomenda;
  onChangeFiltro: (filtro: Partial<FiltroEncomenda>) => any;
  data: PaginateResult<EncomendaDto> | undefined;
  isLoading: boolean;
  setIdEncomendaHistoricoAtual: (id: string) => any;
  setIdEncomendaHistorico: (id: string) => any;
  onChangeView?: () => any;
  view: "CALENDARIO" | "LISTA";
  sorter: (sortBy?: string, direction?: "ASC" | "DESC") => void;
  onDeleteEncomenda?: (id: string) => any;
}

export default function ListaEncomendaTable({
  filtro,
  onChangeFiltro,
  data,
  isLoading,
  setIdEncomendaHistoricoAtual,
  setIdEncomendaHistorico,
  onChangeView,
  view,
  sorter,
  onDeleteEncomenda,
}: ListaEncomendaTableProps) {
  const columns = useEncomendaColumns();
  const { openWithDefaultValues } = useMensagemModal();
  const onUnselect = () => onChangeFiltro({ statusId: undefined });

  return (
    <Box>
      <NewTable
        title={() => (
          <HeaderTable
            value={filtro.search || ""}
            onSearch={(v) => onChangeFiltro({ search: v })}
            childrenFlex={0.4}
          >
            <Flex
              justifyContent="space-between"
              gap={4}
              align="center"
              w="100%"
            >
              <Flex gridGap="16px">
                <SelectableButton
                  selected={
                    filtro.statusId ===
                    EncomendaStatusEnum.EntregaConfirmadaPeloMorador
                  }
                  onClick={() => {
                    onChangeFiltro({
                      statusId:
                        EncomendaStatusEnum.EntregaConfirmadaPeloMorador,
                    });
                  }}
                  onUnselect={onUnselect}
                  rounded="full"
                  selectedColor={colors.green500}
                  fontSize="14px"
                >
                  Entrega confirmada
                </SelectableButton>
                <SelectableButton
                  selected={
                    filtro.statusId === EncomendaStatusEnum.EntregueAoMorador
                  }
                  onClick={() => {
                    onChangeFiltro({
                      statusId: EncomendaStatusEnum.EntregueAoMorador,
                    });
                  }}
                  onUnselect={onUnselect}
                  rounded="full"
                  selectedColor={colors.green500}
                  fontSize="14px"
                >
                  Entregue
                </SelectableButton>

                <SelectableButton
                  selected={
                    filtro.statusId === EncomendaStatusEnum.RecebidaNaPortaria
                  }
                  onClick={() => {
                    onChangeFiltro({
                      statusId: EncomendaStatusEnum.RecebidaNaPortaria,
                    });
                  }}
                  onUnselect={onUnselect}
                  rounded="full"
                  selectedColor={colors.yellow}
                  fontSize="14px"
                >
                  Recebido na portaria
                </SelectableButton>

                <SelectableButton
                  selected={
                    filtro.statusId ===
                    EncomendaStatusEnum.RecebidaNaSalaDeEncomendas
                  }
                  onClick={() => {
                    onChangeFiltro({
                      statusId: EncomendaStatusEnum.RecebidaNaSalaDeEncomendas,
                    });
                  }}
                  onUnselect={onUnselect}
                  rounded="full"
                  selectedColor={colors.yellow}
                  fontSize="14px"
                >
                  Recebido na sala de encomendas
                </SelectableButton>
                <SelectableButton
                  selected={
                    filtro.statusId ===
                    EncomendaStatusEnum.AguardandoRecebimento
                  }
                  onClick={() => {
                    onChangeFiltro({
                      statusId: EncomendaStatusEnum.AguardandoRecebimento,
                    });
                  }}
                  onUnselect={onUnselect}
                  rounded="full"
                  selectedColor={colors.yellow}
                  fontSize="14px"
                >
                  Pendente
                </SelectableButton>
              </Flex>
              <Flex>
                <Button
                  color={colors.primary}
                  bg="none"
                  onClick={onChangeView}
                  gap={2}
                >
                  <FaCalendarDay />
                  Ver{view === "LISTA" ? " no calendário" : " em lista"}
                </Button>
              </Flex>
            </Flex>
          </HeaderTable>
        )}
        onChange={(__, _, sort: any) =>
          sorter(
            order(sort?.order) === undefined ? undefined : sort?.columnKey,
            order(sort?.order)
          )
        }
        columns={columns}
        dataSource={data?.items ?? []}
        loading={isLoading}
        customPagination={{
          currentPage: data?.meta.currentPage ?? 1,
          totalItems: data?.meta.totalItems ?? 0,
          itemsPerPage: data?.meta.itemsPerPage ?? 10,
          onChangePage: (page, limit) => onChangeFiltro?.({ page, limit }),
        }}
        actions={[
          {
            title: "Status da encomenda",
            icon: () => <VscFile size={20} />,
            onClick: (v: EncomendaDto) => setIdEncomendaHistoricoAtual(v.id),
          },
          {
            title: "Enviar mensagem para morador",
            icon: () => <BsChatDots size={20} />,
            onClick: (v) =>
              openWithDefaultValues(
                {
                  tipoDestinoId: TipoDestinoEnum.UmaUnidade,
                  destinoId: v.unidade.id,
                  destinos: [
                    {
                      tipoDestinoId: TipoDestinoEnum.UmaUnidade,
                      destinoId: v.unidade.id!,
                      label: "Essa unidade",
                    },
                  ],
                  assuntoId: AssuntoMensagem.Outros,
                  assuntoCustomizado: `Sobre a encomenda ${v.descricao}`,
                },
                undefined,
                true
              ),
          },
          {
            title: "Histórico",
            icon: () => <MdOutlineHistory size={20} />,
            onClick: (v: EncomendaDto) => setIdEncomendaHistorico(v.id),
          },
          {
            title: "Excluir",
            icon: () => <MdDelete size={20} />,
            permission:
              PermissaoEnum.Backoffice.Administrativo.Encomenda.Excluir,
            shouldRender: () => !!onDeleteEncomenda,
            onClick: (v) => onDeleteEncomenda?.(v.id),
          },
        ]}
      />
    </Box>
  );
}

function order(ord: "ascend" | "descend" | undefined) {
  switch (ord) {
    case "ascend":
      return "ASC";
    case "descend":
      return "DESC";
    default:
      return undefined;
  }
}
